<script lang="ts">
	import { real_id } from 'src/helpers/interface_helpers';
	import { onDestroy } from 'svelte';
	import { format } from 'svelte-i18n';
	import {
		Alert,
		Button,
		CardGrid,
		Checkbox,
		Input,
		Loader,
		SelectCard,
		Text,
		toast,
	} from '@Shelly/shelly-design-system';
	import {
		buildManualDskParsedData,
		formatDskToDashed,
		isValidDSK,
		provisionSmartStartNode,
		startZWaveExclusion,
		startZWaveInclusion,
		stopZWaveLearnMode,
	} from 'src/helpers/z_wave_qr_helpers';
	import { read_qr_code } from 'src/services/http';
	import { mobile } from 'src/services/mobile-rework';
	import { os, devices_status, includingDeviceData, zwavePendingAssociation } from 'src/stores';
	import type { Device } from 'src/types/Device';

	export let device: Device;

	type InclusionMethod = 'qr' | 'manual';
	type InclusionAction = 'include' | 'exclude';
	type ManualSecurity = 'unsecure' | 'secure';
	type QrEntryMode = 'idle' | 'camera' | 'dsk';

	const LEARN_MODE_MS = 30_000;

	const securityClassLabels: Record<number, string> = {
		'-2': 'zwave_security_temporary',
		'-1': 'zwave_security_none',
		0: 'zwave_security_unauthenticated',
		1: 'zwave_security_authenticated',
		2: 'zwave_security_access_control',
		7: 'zwave_security_legacy',
	};

	const securityClassDefaults: Record<number, string> = {
		'-2': 'Temporary',
		'-1': 'None',
		0: 'Unauthenticated',
		1: 'Authenticated',
		2: 'Access control',
		7: 'Legacy',
	};

	$: status = $devices_status?.[real_id(device?.id)];
	$: isOnline = status?.online;

	let method: InclusionMethod = 'qr';
	let action: InclusionAction = 'include';
	let longRange = false;
	let manualSecurity: ManualSecurity = 'unsecure';
	let s2Pin = '';
	let dskInput = '';
	let qrEntryMode: QrEntryMode = 'idle';
	let loading = false;
	let learnModeActive = false;
	let learnModeTimer: ReturnType<typeof setTimeout> | undefined;

	$: localParsedData = $includingDeviceData?.zWaveParsedData;
	$: selectedSecurities = localParsedData?.securityClasses ?? [];
	$: securityClasses = localParsedData?.validateSecurityClasses;
	$: hasParsedData = typeof localParsedData !== 'undefined';
	$: hasParseError = hasParsedData && Object.keys(localParsedData).includes('error');
	$: showQrSecurityStep = method === 'qr' && action === 'include' && hasParsedData && !hasParseError;
	$: dskIsValid = isValidDSK(dskInput);
	$: pinIsValid = /^\d{5}$/.test(s2Pin);

	$: helpText = (() => {
		if (action === 'exclude') {
			return $format('zwave_help_exclude_manual', {
				default:
					'Put your Z-Wave device into exclusion mode, then click Start to begin exclusion.',
			});
		}
		if (method === 'manual') {
			return $format('zwave_help_include_manual', {
				default:
					'Put your Z-Wave device into pairing mode, then click Start to begin inclusion.',
			});
		}
		return $format('zwave_help_include_qr', {
			default: "Scan the QR code on your Z-Wave device's label to add it.",
		});
	})();

	$: startDisabled = (() => {
		if (!isOnline || loading) return true;
		if (learnModeActive) return true;
		if (action === 'exclude') return false;
		if (method === 'manual') {
			return manualSecurity === 'secure' && !pinIsValid;
		}
		return !hasParsedData || hasParseError || selectedSecurities.length === 0;
	})();

	const getSecurityLabel = (index: number) => {
		const key = securityClassLabels[index];
		return $format(key, { default: securityClassDefaults[index] ?? String(index) });
	};

	const clearLearnModeTimer = () => {
		if (learnModeTimer) {
			clearTimeout(learnModeTimer);
			learnModeTimer = undefined;
		}
	};

	const setLearnModeActive = (active: boolean) => {
		learnModeActive = active;
		clearLearnModeTimer();
		if (active) {
			learnModeTimer = setTimeout(() => {
				learnModeActive = false;
				learnModeTimer = undefined;
			}, LEARN_MODE_MS);
		}
	};

	const resetQrParsedData = () => {
		includingDeviceData.updateData({ zWaveParsedData: undefined });
		qrEntryMode = 'idle';
		dskInput = '';
	};

	const validateSecurityCheckboxes = () => {
		if (typeof securityClasses === 'undefined' || !localParsedData) return;

		Object.values(securityClasses).forEach((security) => {
			const requested = localParsedData.requestedSecurityClasses ?? [];
			if (!requested.includes(security.index)) {
				securityClasses[security.index].disabled = true;
			} else {
				securityClasses[security.index].disabled = false;
			}
			if (selectedSecurities.length === 1 && selectedSecurities.includes(security.index)) {
				securityClasses[security.index].disabled = true;
			} else if (selectedSecurities.includes(security.index)) {
				securityClasses[security.index].disabled = false;
			}
		});
	};

	const selectSecurityOption = (option: number) => {
		if (!localParsedData) return;
		const nextSelected = [...selectedSecurities];
		if (nextSelected.includes(option)) {
			nextSelected.splice(nextSelected.indexOf(option), 1);
		} else {
			nextSelected.push(option);
		}
		includingDeviceData.updateData({
			zWaveParsedData: {
				...localParsedData,
				securityClasses: nextSelected,
			},
		});
		validateSecurityCheckboxes();
	};

	const startCameraScan = () => {
		if (!mobile.isMobile) return;
		qrEntryMode = 'camera';
		includingDeviceData.updateData({ zWaveParsedData: undefined });
		const scanDelayMs = $os === 'IOS' ? 400 : 0;
		setTimeout(() => read_qr_code(), scanDelayMs);
	};

	const confirmDsk = () => {
		if (!dskIsValid) return;
		buildManualDskParsedData(dskInput);
		qrEntryMode = 'idle';
	};

	const onDskInput = (event: Event) => {
		const target = event.target as HTMLInputElement;
		dskInput = formatDskToDashed(target.value);
	};

	const onS2PinInput = (event: Event) => {
		const target = event.target as HTMLInputElement;
		s2Pin = target.value.replace(/\D/g, '').slice(0, 5);
	};

	const setPendingAssociation = (payload: { dsk?: string; productId?: string; manual?: boolean }) => {
		zwavePendingAssociation.set({
			...payload,
			gatewayId: device.id,
		});
	};

	const handleQrProvision = async () => {
		if (!localParsedData?.dsk || hasParseError) return;

		loading = true;
		const response = await provisionSmartStartNode(
			device,
			{
				qr: localParsedData.qr,
				dsk: localParsedData.dsk,
				securityClasses: localParsedData.securityClasses ?? [1],
			},
			longRange,
		);
		loading = false;

		if (!response?.isok) {
			toast.open({
				type: 'danger',
				title: $format('zwave_inclusion_failed', {
					default: 'Z-Wave inclusion failed. Please try again.',
				}),
			});
			return;
		}

		setPendingAssociation({
			dsk: localParsedData.dsk,
			productId: localParsedData.productId,
		});

		toast.open({
			type: 'success',
			title: $format('zwave_provision_success', {
				default: 'Device provisioned. Power it on — it will appear in the gateway room.',
			}),
		});
		resetQrParsedData();
	};

	const handleManualStart = async () => {
		loading = true;
		const inclusionParams = manualSecurity === 'secure' && pinIsValid ? { pin: s2Pin } : {};
		const response =
			action === 'exclude'
				? await startZWaveExclusion(device)
				: await startZWaveInclusion(device, inclusionParams);
		loading = false;

		if (!response?.isok) {
			toast.open({
				type: 'danger',
				title: $format(
					action === 'exclude' ? 'zwave_exclusion_failed' : 'zwave_inclusion_failed',
					{
						default:
							action === 'exclude'
								? 'Z-Wave exclusion failed. Please try again.'
								: 'Z-Wave inclusion failed. Please try again.',
					},
				),
			});
			return;
		}

		if (action === 'include') {
			setPendingAssociation({ manual: true });
		}

		setLearnModeActive(true);
		toast.open({
			type: 'info',
			title: $format(
				action === 'exclude' ? 'zwave_exclusion_started' : 'zwave_inclusion_started',
				{
					default:
						action === 'exclude'
							? 'Exclusion window started. Put the device in exclusion mode.'
							: 'Inclusion window started. Put the device in pairing mode.',
				},
			),
		});
	};

	const handleStart = async () => {
		if (method === 'qr' && action === 'include') {
			await handleQrProvision();
			return;
		}
		await handleManualStart();
	};

	const handleStop = async () => {
		loading = true;
		const response = await stopZWaveLearnMode(device);
		loading = false;

		if (!response?.isok) {
			toast.open({
				type: 'danger',
				title: $format('zwave_stop_learn_mode_failed', {
					default: 'Failed to stop pairing window.',
				}),
			});
			return;
		}

		setLearnModeActive(false);
		zwavePendingAssociation.set(undefined);
		toast.open({
			type: 'success',
			title: $format('zwave_stop_learn_mode_success', {
				default: 'Pairing window stopped.',
			}),
		});
	};

	const selectMethod = (nextMethod: InclusionMethod) => {
		method = nextMethod;
		if (nextMethod === 'manual') {
			resetQrParsedData();
		}
	};

	const selectAction = (nextAction: InclusionAction) => {
		action = nextAction;
		if (nextAction === 'exclude') {
			method = 'manual';
			resetQrParsedData();
		}
	};

	$: (selectedSecurities.length, validateSecurityCheckboxes());

	onDestroy(() => {
		clearLearnModeTimer();
	});
</script>

{#if !isOnline}
	<Alert
		type="warning"
		heading={$format('alert_heading_device_offline', { default: 'Device offline' })}
		message={$format('alert_message_zwave_gateway_offline', {
			default: 'The gateway must be online to include or exclude Z-Wave devices.',
		})}
	/>
{:else}
	<Text styleType="body-small" class="mb-xs text-bold">
		{$format('label_method', { default: 'Method' })}
	</Text>
	<CardGrid sm={160} gridGap="s" noPaddingBottom class="mb-m">
		<SelectCard
			type="horizontal"
			variant="radio"
			class="width-full"
			title={$format('zwave_method_qr', { default: 'QR Code (device label)' })}
			selected={method === 'qr'}
			disabled={action === 'exclude'}
			on:click={() => selectMethod('qr')}
		/>
		<SelectCard
			type="horizontal"
			variant="radio"
			class="width-full"
			title={$format('zwave_method_manual', { default: 'Manual' })}
			selected={method === 'manual'}
			on:click={() => selectMethod('manual')}
		/>
	</CardGrid>

	<Text styleType="body-small" class="mb-xs text-bold">
		{$format('label_action', { default: 'Action' })}
	</Text>
	<CardGrid sm={160} gridGap="s" noPaddingBottom class="mb-m">
		<SelectCard
			type="horizontal"
			variant="radio"
			class="width-full"
			title={$format('zwave_action_include', { default: 'Include' })}
			selected={action === 'include'}
			on:click={() => selectAction('include')}
		/>
		<SelectCard
			type="horizontal"
			variant="radio"
			class="width-full"
			title={$format('zwave_action_exclude', { default: 'Exclude' })}
			selected={action === 'exclude'}
			on:click={() => selectAction('exclude')}
		/>
	</CardGrid>

	{#if method === 'manual' && action === 'include'}
		<Text styleType="body-small" class="mb-xs text-bold">
			{$format('label_security', { default: 'Security' })}
		</Text>
		<CardGrid sm={160} gridGap="s" noPaddingBottom class="mb-m">
			<SelectCard
				type="horizontal"
				variant="radio"
				class="width-full"
				title={$format('zwave_security_unsecure', { default: 'Unsecure' })}
				selected={manualSecurity === 'unsecure'}
				on:click={() => (manualSecurity = 'unsecure')}
			/>
			<SelectCard
				type="horizontal"
				variant="radio"
				class="width-full"
				title={$format('zwave_security_secure_pin', { default: 'Secure (PIN)' })}
				selected={manualSecurity === 'secure'}
				on:click={() => (manualSecurity = 'secure')}
			/>
		</CardGrid>
		{#if manualSecurity === 'secure'}
			<Input
				id="zwave-s2-pin"
				class="mb-m"
				label={$format('zwave_s2_pin_label', { default: 'S2 PIN (5 digits)' })}
				placeholder="12345"
				value={s2Pin}
				maxlength={5}
				inputmode="numeric"
				aria-label={$format('zwave_s2_pin_label', { default: 'S2 PIN (5 digits)' })}
				error={s2Pin.length > 0 && !pinIsValid
					? $format('zwave_s2_pin_invalid', { default: 'PIN must be exactly 5 digits' })
					: undefined}
				on:input={onS2PinInput}
			/>
		{/if}
	{/if}

	{#if method === 'qr' && action === 'include'}
		<Checkbox
			id="zwave-long-range"
			class="mb-s"
			checked={longRange}
			on:click={() => (longRange = !longRange)}
		>
			{$format('zwave_long_range', { default: 'Long Range' })}
		</Checkbox>
		<Text styleType="body-small" class="mb-m text-color-muted">
			{$format('zwave_long_range_hint', {
				default: 'Enable Z-Wave Long Range for extended coverage',
			})}
		</Text>

		{#if showQrSecurityStep && securityClasses}
			<Text class="mb-xs">
				{$format('zwave_security_classes_prompt', {
					default: 'Choose which security classes to grant the new node:',
				})}
			</Text>
			{#each Object.values(securityClasses) as security (security.index)}
				<Checkbox
					id={'gateway-zwave-security-' + security.index}
					class="mb-xs"
					checked={selectedSecurities.includes(security.index)}
					disabled={security.disabled}
					on:click={() => selectSecurityOption(security.index)}
				>
					{getSecurityLabel(security.index)}
				</Checkbox>
			{/each}
		{:else if hasParseError}
			<Alert
				class="mb-m"
				type="warning"
				message={$format('zwave_qr_parse_failed', {
					default: 'Could not read the QR code. Check the code and try again.',
				})}
			/>
		{:else if qrEntryMode === 'camera'}
			<Loader class="mb-m" />
		{:else}
			{#if mobile.isMobile}
				<Button class="mb-s width-full" variant="secondary" on:click={startCameraScan}>
					{$format('zwave_scan_qr_camera', { default: 'Scan QR with camera' })}
				</Button>
			{/if}
			<Input
				id="zwave-gateway-dsk"
				class="mb-s"
				label={$format('zwave_dsk_input_label', { default: 'Device DSK' })}
				aria-label={$format('zwave_dsk_input_label', { default: 'Device DSK' })}
				placeholder="#####-#####-#####-#####-#####-#####-#####-#####"
				value={dskInput}
				error={dskInput.length > 0 && !dskIsValid
					? $format('zwave_dsk_invalid', { default: 'Invalid DSK format' })
					: undefined}
				on:input={onDskInput}
			/>
			<Button
				class="mb-m width-full"
				variant="secondary"
				disabled={!dskIsValid}
				on:click={confirmDsk}
			>
				{$format('zwave_use_dsk', { default: 'Use DSK' })}
			</Button>
		{/if}
	{/if}

	<Text styleType="body-small" class="mb-m text-color-muted">
		{helpText}
	</Text>

	{#if learnModeActive}
		<Button class="width-full" variant="secondary" {loading} on:click={handleStop}>
			{$format('button_stop', { default: 'Stop' })}
		</Button>
	{:else}
		<Button class="width-full" {loading} disabled={startDisabled} on:click={handleStart}>
			{$format('button_start', { default: 'Start' })}
		</Button>
	{/if}
{/if}
